import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { SUBDOMAIN } from "../../../assets/meta/meta";
import Navbar from "../../../Components/Common/navbar";
import { API, BACKEND_HOST } from "../../../api";
import qs from "qs";
import BgFrame from "../../../Components/Common/BgFrame";
import { getAccessToken } from "../../../api/auth";
import { useAuthContext } from "../../../Components/AuthContext";
import MobileViewContainer from "../../../Components/MobileViewContainer";
import { usePGModules, useTossPaymentWidget, useOrderAgreements, dispatchPayment } from "../../../common/payments";
import { OrdererInfoSection, CollapsibleSection, RefundPolicySection, PGSelectorSection, TotalPriceDisplay, PayButton } from "../components";

const AddOptionsOrder = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const query = qs.parse(location.search, { ignoreQueryPrefix: true });
  const { id, deposit } = query;

  const [orderData, setOrderData] = useState({
    reservationName: "",
    spotName: "",
    reservDate: "",
    reservId: "",
    options: [],
  });

  const { userInfo } = useAuthContext();

  // --- 공통 훅 ---
  const { pgModules, selectedPG, setSelectedPG } = usePGModules();
  const {
    cancelRuleAgree, purchaseOneAgree,
    setCancelRuleAgree, togglePurchaseAgreeOptions, allAgreed,
  } = useOrderAgreements();

  // --- 가격 ---
  const totalOrderPrice = useMemo(() => {
    return orderData.options
      .map((item) => parseInt(item.price * item.quantity))
      .reduce((sum, curr) => sum + curr, 0);
  }, [orderData.options]);

  const totalTaxfreePrice = useMemo(() => {
    return orderData.options
      .map((item) => (item.taxfree ? parseInt(item.price * item.quantity) : 0))
      .reduce((sum, curr) => sum + curr, 0);
  }, [orderData.options]);

  // --- Toss 위젯 ---
  const { paymentWidgetRef } = useTossPaymentWidget({
    userInfo,
    totalOrderPrice,
    isTossSelected: selectedPG?.code === "toss",
  });

  // --- 데이터 로드 ---
  useEffect(() => {
    if (location.state && location.state.reservId) {
      setOrderData(location.state);
    }
  }, [location.state]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (id && deposit) {
          const accessToken = getAccessToken();
          const reservationResponse = await API.get(`/package/get-reservation/${id}/`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          });
          const reservationData = reservationResponse.data;
          if (!reservationData.package_id) { toast.error("예약 정보 로드 실패.."); return; }

          const itemsResponse = await API.get(`/package/items/${reservationData.package_id}/`);
          const depositOption = itemsResponse.data.options.find((o) => o.type === "deposit");
          setOrderData({
            reservationName: reservationData.name,
            spotName: reservationData.spot,
            reservDate: reservationData.reservationDate,
            reservId: reservationData.id,
            options: depositOption
              ? [{ ...depositOption, reservId: reservationData.id, optionId: depositOption.id }]
              : [],
          });
        }
      } catch (error) {
        console.error(error);
      }
    };
    fetchData();
  }, [id, deposit]);

  // --- 결제 ---
  const processPayment = async () => {
    const accessToken = getAccessToken();
    if (!userInfo) { toast.warning("로그인이 필요한 서비스입니다."); navigate(`/login`); return; }
    if (totalOrderPrice === 0) { toast.warning("결제가능한 최소금액은 1,000원입니다."); return; }

    try {
      const _body = {
        options: orderData.options.flatMap((item) =>
          Array(item.quantity).fill().map(() => ({ reserv_id: item.reservId, option_id: item.optionId }))
        ),
      };
      const response = await API.post("/package/pre-order-option/", _body, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const data = response.data;
      if ("error" in data) throw new Error(data.error);

      await dispatchPayment({
        selectedPG,
        paymentWidgetRef,
        orderId: data.orderId,
        orderName: `${orderData.reservationName}_추가옵션`,
        amount: totalOrderPrice,
        userInfo,
        taxFreeAmount: totalTaxfreePrice,
        urls: {
          successUrl: `${BACKEND_HOST}/package/order-option/`,
          failUrl: `https://${SUBDOMAIN}.${window.AMZ_ENV.VITE_DOMAIN}/my-page`,
          niceReturnUrl: "/package/order-option-nice/",
          hectoNotiUrl: `${BACKEND_HOST}/package/order-option-hecto/`,
          hectoNextUrl: `${BACKEND_HOST}/package/order-done-hecto/`,
          hectoCancUrl: `${BACKEND_HOST}/package/order-done-hecto/`,
        },
        failNavigate: () => navigate("/my-page"),
      });
    } catch (error) {
      console.error(error);
      toast.error("추가옵션을 결제하는데 실패했습니다.");
      navigate("/my-page");
    }
  };

  // --- 로딩 ---
  if (orderData.options.length === 0) {
    return (
      <MobileViewContainer>
        <Navbar />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">로딩중입니다...</div>
        </div>
      </MobileViewContainer>
    );
  }

  return (
    <MobileViewContainer>
      <Navbar />
      <BgFrame>
        <div className="space-y-2.5">
          {/* 주문자 */}
          <OrdererInfoSection userInfo={userInfo} />

          {/* 주문상품 */}
          <CollapsibleSection title="주문상품" defaultOpen>
            <div>
              <span className="fw-semibold font-gmarket">{orderData.reservationName} 추가 옵션</span>
              {location.state?.reservDate && (
                <span className="block mt-1 mb-1">예약일 {location.state.reservDate}</span>
              )}
              {orderData.options.map((option, oidx) => (
                <div key={oidx} className="border-b border-gray-200 py-2.5">
                  <span>
                    <span className="font-gmarket text-brand">{orderData.spotName}</span>번 {option.price.toLocaleString()}원
                  </span>
                  <div className="text-right">
                    <p className="text-muted text-sm mb-1">
                      {option.name} X {option.quantity}개
                      <span className="ml-1.5">{(option.price * option.quantity).toLocaleString()}원</span>
                    </p>
                  </div>
                </div>
              ))}
              <TotalPriceDisplay amount={totalOrderPrice} label="총 금액" />
            </div>
          </CollapsibleSection>

          {/* 환불 규정 */}
          <RefundPolicySection
            type={deposit ? "deposit" : "options"}
            cancelRuleAgree={cancelRuleAgree}
            purchaseOneAgree={purchaseOneAgree}
            onCancelRuleChange={setCancelRuleAgree}
            onPurchaseAgreeChange={togglePurchaseAgreeOptions}
          />

          {/* PG 선택 */}
          <PGSelectorSection pgModules={pgModules} selectedPG={selectedPG} onSelect={setSelectedPG} />

          {/* 하단 여백 */}
          <div style={{ height: "100px" }} />
        </div>

        {/* 결제 버튼 */}
        <PayButton
          onClick={() => {
            if (!allAgreed) { toast.warning("동의항목을 확인해주세요."); return; }
            processPayment();
          }}
        />
      </BgFrame>
    </MobileViewContainer>
  );
};

export default AddOptionsOrder;
